const TAB_ID_STORAGE_KEY = 'ot-tab-id';

let cachedTabId: string | null = null;

/**
 * Returns a stable tab identifier that persists across same-tab navigations
 * and reloads, but differs across tabs. `x-tab-id` is sent on every
 * bot-environments request (mirrors aistudio's own `x-tab-id` header, sent
 * unconditionally by `client/services/http.js`) — the backend doesn't need
 * this to match aistudio's own `ais-tab-`-prefixed id, just to be present and
 * stable per tab.
 *
 * Stored in `sessionStorage` (per-tab, per-origin), NOT `window.name`: the
 * federation mount shares the aistudio host's window, and the host's own
 * tab-id service keeps an `ais-tab-`-prefixed id in `window.name` with a
 * check-and-regenerate pattern. Two writers with different prefixes on one
 * shared `window.name` would reject each other's value and mint a fresh UUID
 * on every read. Ported from agent-tools' `shared/lib/tab-id.ts`, which hit
 * the same constraint.
 */
export function getTabId(): string {
  if (typeof window === 'undefined') return 'ot-tab-ssr';
  if (cachedTabId) return cachedTabId;

  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(TAB_ID_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable — fall back to the in-memory id below.
  }

  if (!stored || !stored.startsWith('ot-tab-')) {
    stored = `ot-tab-${crypto.randomUUID()}`;
    try {
      window.sessionStorage.setItem(TAB_ID_STORAGE_KEY, stored);
    } catch {
      // Keep the in-memory id; it stays stable until the page unloads.
    }
  }

  cachedTabId = stored;
  return stored;
}
